import GooglePlacesSDK from 'react-native-google-places-sdk'
import Config from 'react-native-config'
import { isEmpty } from 'lodash'
import { Platform } from 'react-native'
import { Bugsnag, Device } from '@config'
import { AddressCountries } from '@src/types'
import { parseGooglePlace } from '@utils/parseGooglePlace'

const isNativeSupported = Device.ios || Device.android

if (isNativeSupported) {
  GooglePlacesSDK.initialize(
    Platform.select({
      ios: Config.IOS_GOOGLE_API_KEY,
      android: Config.ANDROID_GOOGLE_API_KEY,
    })!,
  )
}

export interface Place {
  description: string
  place_id: string
}

const countryFilter = (country = AddressCountries.Us) => ({
  countries: [country.toLowerCase()],
})

const fetchPlaces = async (street: string, queryOverrides: ReturnType<typeof countryFilter>) => {
  if (isEmpty(street) || !isNativeSupported) {
    return []
  }

  try {
    const predictions = await GooglePlacesSDK.fetchPredictions(street, {
      countries: queryOverrides.countries,
      types: ['address'],
    })

    return predictions.map((prediction) => ({
      description: prediction.description,
      place_id: prediction.placeID,
    }))
  } catch (error: any) {
    Bugsnag.notify(error)
    return []
  }
}

const fetchPlaceDetail = async (placeId: string) => {
  if (isEmpty(placeId) || !isNativeSupported) {
    return {}
  }

  try {
    const place = await GooglePlacesSDK.fetchPlaceByID(placeId)
    return place
  } catch (error: any) {
    Bugsnag.notify(error)
    return {}
  }
}

const fetchPlacesForStreetAndCountry = async ({
  street,
  country,
}: {
  street: string
  country: AddressCountries
}) => {
  const places = await fetchPlaces(street, countryFilter(country))
  return places.map(({ description, place_id: placeId }) => ({ description, place_id: placeId }))
}

const fetchAddressForPlace = async ({ place }: { place: Place }) => {
  const data = await fetchPlaceDetail(place.place_id)
  return extractAddressFromPlace(data)
}

const extractAddressFromPlace = (place: any) => {
  const parsed = parseGooglePlace(place)

  return {
    street: parsed.address,
    city: parsed.city,
    state: parsed.stateShort,
    zipCode: parsed.zipCode,
  }
}

export default {
  fetchPlacesForStreetAndCountry,
  fetchAddressForPlace,
}
